import { QueryClient } from '@tanstack/react-query';

import { useSearchHospitalGenerallyQuery } from '@/hooks/hospital/list/hospitalList.gql.impl';
import { NeighborhoodHospitalFilter, NeighborhoodHospitalType } from '@/models/hospital';
import {
  HospitalSearchCommonFilter,
  getCoordinate,
  getHospitalCommonFilter,
  getHospitalSearchSortFilter,
} from '@/parsers/f2f/common';
import * as Types from '@/types/bff/gql-schema';
import { HospitalCommonFilterType, HospitalSearchQueries } from '@/types/hospital';
import { getCommonFilterQueryString } from '@/utils/format';

import useNavigate from '../../useNavigate';
import useSearchHospitalsGenerallyInfinityQuery from './useSearchHospitalsGenerallyInfinityQuery';

const useSearchHospitalsGenerally = (
  queries: HospitalSearchQueries & {
    regionByDepth?: Types.HospitalElasticSearchRegionByDepthRequestInput;
    size?: number;
    regionCode?: string;
  }
) => {
  const { keyword = '', latitude, longitude, sortType, regionByDepth, commonFilter, regionCode, size = 30 } = queries;
  const navigate = useNavigate();
  const queryRequest: Types.HospitalGeneralSearchRequestInput = {
    geoPoint: getCoordinate(latitude, longitude),
    limit: size,
    keyword: decodeURIComponent(keyword),
    sortType: getHospitalSearchSortFilter(sortType).dtoType,
    regionByDepth,
    commonFilter: getHospitalCommonFilter(commonFilter).values,
    regionCode,
  };

  const { data, isLoading, fetchNextPage, hasNextPage } = useSearchHospitalsGenerallyInfinityQuery(queryRequest);
  const hospitals = data ?? [];

  const goToAppHospital = (hospitalPid: string) => {
    navigate.navigateToHospitalDetail({ hospitalPid });
  };

  return {
    isVisibleDeleteIcon: !!keyword,
    isLoadingHospitals: isLoading,
    hospitals: data ?? [],
    hasNoData: !hospitals.length,
    fetchNextPage: fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    goToAppHospital,
  };
};

export default useSearchHospitalsGenerally;

useSearchHospitalsGenerally.prefetchInfinityQuery = async (
  queryClient: QueryClient,
  params: HospitalSearchQueries & {
    regionByDepth?: Types.HospitalElasticSearchRegionByDepthRequestInput;
    size?: number;
    regionCode?: string;
  }
) => {
  const { keyword = '', latitude, longitude, sortType, regionByDepth, commonFilter, regionCode, size = 30 } = params;

  const queryRequest: Types.HospitalGeneralSearchRequestInput = {
    geoPoint: getCoordinate(latitude, longitude),
    limit: size,
    keyword: decodeURIComponent(keyword),
    sortType: getHospitalSearchSortFilter(sortType).dtoType,
    regionByDepth,
    commonFilter: getHospitalCommonFilter(commonFilter).values,
    regionCode,
  };

  await queryClient.prefetchInfiniteQuery(useSearchHospitalGenerallyQuery.getKey({ request: queryRequest }), () =>
    useSearchHospitalGenerallyQuery.fetcher({ request: queryRequest })()
  );
};

useSearchHospitalsGenerally.parseParameterByNeighborhoodHospital = (params: {
  keyword: string;
  regionCode: string;
  filter?: NeighborhoodHospitalFilter;
  hospitalType: NeighborhoodHospitalType;
  size?: number;
}): {
  commonFilter?: string;
  keyword: string;
  regionCode: string;
  sortType: string;
  size: number;
} => {
  const { filter, hospitalType, keyword, regionCode, size = 10 } = params;
  const sortType = 'REVIEW_AVERAGE_RATING';

  switch (hospitalType) {
    case NeighborhoodHospitalType.ALL: {
      const hospitalCommonFilter = new HospitalSearchCommonFilter({
        [HospitalCommonFilterType.NonF2f]: filter === NeighborhoodHospitalFilter.NonF2fHospital,
        [HospitalCommonFilterType.Bookable]: false,
        [HospitalCommonFilterType.Operating]: false,
        [HospitalCommonFilterType.NightOperation]: false,
        [HospitalCommonFilterType.Specialist]: false,
        [HospitalCommonFilterType.SundayOperation]: false,
      });

      return {
        commonFilter: getCommonFilterQueryString(hospitalCommonFilter.values),
        keyword,
        regionCode,
        sortType,
        size,
      };
    }
    case NeighborhoodHospitalType.NightOperation: {
      const hospitalCommonFilter = new HospitalSearchCommonFilter({
        [HospitalCommonFilterType.NonF2f]: filter === NeighborhoodHospitalFilter.NonF2fHospital,
        [HospitalCommonFilterType.Bookable]: false,
        [HospitalCommonFilterType.Operating]: false,
        [HospitalCommonFilterType.NightOperation]: true,
        [HospitalCommonFilterType.Specialist]: false,
        [HospitalCommonFilterType.SundayOperation]: false,
      });
      return {
        commonFilter: getCommonFilterQueryString(hospitalCommonFilter.values),
        keyword,
        regionCode,
        sortType,
        size,
      };
    }
    case NeighborhoodHospitalType.SundayOperation: {
      const hospitalCommonFilter = new HospitalSearchCommonFilter({
        [HospitalCommonFilterType.NonF2f]: filter === NeighborhoodHospitalFilter.NonF2fHospital,
        [HospitalCommonFilterType.Bookable]: false,
        [HospitalCommonFilterType.Operating]: false,
        [HospitalCommonFilterType.NightOperation]: false,
        [HospitalCommonFilterType.Specialist]: false,
        [HospitalCommonFilterType.SundayOperation]: true,
      });
      return {
        commonFilter: getCommonFilterQueryString(hospitalCommonFilter.values),
        keyword,
        regionCode,
        sortType,
        size,
      };
    }
    case NeighborhoodHospitalType.Specialist: {
      const hospitalCommonFilter = new HospitalSearchCommonFilter({
        [HospitalCommonFilterType.NonF2f]: filter === NeighborhoodHospitalFilter.NonF2fHospital,
        [HospitalCommonFilterType.Bookable]: false,
        [HospitalCommonFilterType.Operating]: false,
        [HospitalCommonFilterType.NightOperation]: false,
        [HospitalCommonFilterType.Specialist]: true,
        [HospitalCommonFilterType.SundayOperation]: false,
      });
      return {
        commonFilter: getCommonFilterQueryString(hospitalCommonFilter.values),
        keyword,
        regionCode,
        sortType,
        size,
      };
    }
  }
};
